import { getInitials } from "@/utils/generic";
import { useMemo } from "react";
import { NoxusTooltip } from "./NoxusTooltip";
import { cn } from "@/lib/utils";
import { NEUTRAL_COLOUR_TOKEN, resolveColourToken } from "@/utils/colourTokens";

interface IEntityIcon {
  value: string;
  color?: string;
}

interface INoxusEntityIcon extends IEntityIcon {
  hasBorder?: boolean;
  isSmall?: boolean;
  isBig?: boolean;
  isFullValue?: boolean;
  isHovered?: boolean;
  // Render in the neutral grey instead of resolving `color` — used by the
  // stack's "+N" overflow badge, which is chrome, not an entity colour.
  neutral?: boolean;
}

const NoxusEntityIcon = ({
  value,
  color,
  hasBorder,
  isSmall,
  isBig,
  isFullValue,
  isHovered,
  neutral,
}: INoxusEntityIcon) => {
  const initials = useMemo(() => getInitials(value), [value]);
  const token = neutral ? NEUTRAL_COLOUR_TOKEN : resolveColourToken(color);

  return (
    <div
      className={cn(
        "flex min-w-7 h-7 items-center justify-center rounded-[4px] border border-black/10 transition-all duration-200 dark:border-white/15",
        isBig && "min-w-8 min-h-8 w-8 h-8",
        isSmall && "min-w-6 min-h-6 w-6 h-6",
        hasBorder && "ring-2 ring-white dark:ring-background",
        isHovered && "ring-2 ring-accent"
      )}
      style={{ backgroundColor: token.tint }}
    >
      <span
        className={cn(
          "text-sm font-medium leading-none select-none",
          isBig && "text-md",
          isSmall && "text-[0.6rem] tracking-wide"
        )}
        style={{ color: token.text }}
      >
        {isFullValue ? value : initials}
      </span>
    </div>
  );
};

const NoxusEntityIconStack = ({
  elementsArray,
  max = 4,
  isHovered,
}: {
  elementsArray: IEntityIcon[];
  max?: number;
  isHovered?: boolean;
}) => {
  const visibleCount = Math.min(elementsArray.length, max);
  const iconSize = 24;
  const overlap = 4;
  const width = iconSize + (visibleCount - 1) * (iconSize - overlap);

  return (
    <NoxusTooltip
      asChild
      content={
        <div className="flex flex-col justify-center w-full items-start truncate">
          {elementsArray.slice(0, 10).map((el, index) => (
            <span
              key={`entity-${index}`}
              className="w-fit h-fit truncate whitespace-nowrap text-ellipsis overflow-hidden max-w-60"
            >
              {el.value}
            </span>
          ))}
          {elementsArray.length > 10 && (
            <span className="text-muted-foreground">
              +{elementsArray.length - 10} other workspace
              {elementsArray.length > 11 ? "s" : ""}
            </span>
          )}
        </div>
      }
      placement="bottom"
    >
      <div
        className="relative flex flex-row justify-start"
        style={{ width: `${width}px` }}
      >
        {elementsArray.slice(0, max).map((el, index) => {
          const isLastElement = index === max - 1 && elementsArray.length > max;

          return (
            <div
              key={`element-${index}`}
              style={{
                zIndex: max - index,
                marginLeft: index === 0 ? 0 : -overlap,
              }}
            >
              {isLastElement ? (
                <NoxusEntityIcon
                  value={
                    elementsArray.length >= 103
                      ? "+99"
                      : `${elementsArray.length - 3}`
                  }
                  neutral
                  hasBorder
                  isFullValue
                  isSmall
                  isHovered={isHovered}
                />
              ) : (
                <NoxusEntityIcon
                  value={el.value}
                  color={el.color}
                  hasBorder
                  isSmall
                  isHovered={isHovered}
                />
              )}
            </div>
          );
        })}
      </div>
    </NoxusTooltip>
  );
};

export { NoxusEntityIcon, NoxusEntityIconStack };
